import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/components";
import { Input } from "@/shared/components";
import { Label as LabelWidget } from "@/shared/components";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { type Label } from "@/entities/label";

const labelFormSchema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    slug: z.string().optional(),
    color: z.string().optional(),
});

type LabelFormData = z.infer<typeof labelFormSchema>;

interface LabelFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    labelId?: string;
    onSubmit?: (data: LabelFormData) => void;
    initialData?: Partial<Label>;
}

export function LabelFormDialog({ 
    open, 
    onOpenChange, 
    labelId, 
    onSubmit: onSubmitCallback,
    initialData 
}: LabelFormDialogProps) {
    const { t } = useTranslation(['common', 'settings']);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditMode = !!labelId || !!initialData;

    const form = useForm<LabelFormData>({
        resolver: zodResolver(labelFormSchema),
        defaultValues: {
            name: "",
            slug: "",
            color: "#3B82F6",
        },
    });

    useEffect(() => {
        if (open && isEditMode && initialData) {
            form.reset({
                name: initialData.name || "",
                slug: initialData.slug || "",
                color: initialData.color || "#3B82F6",
            });
        } else if (open && !isEditMode) {
            form.reset({
                name: "",
                slug: "",
                color: "#3B82F6",
            });
        }
    }, [open, isEditMode, initialData, form]);

    const onSubmit = async (data: LabelFormData) => {
        setIsSubmitting(true);
        try {
            if (onSubmitCallback) {
                onSubmitCallback(data);
            }
        } catch (_error) {
            toast.error(t('common:errors.saveFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isEditMode ? t('settings:labels.editLabel') : t('settings:labels.addLabel')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditMode
                            ? t('settings:labels.editLabelDescription')
                            : t('settings:labels.addLabelDescription')
                        }
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <LabelWidget htmlFor="name">{t('settings:labels.form.name')} *</LabelWidget>
                        <Input
                            id="name"
                            {...form.register("name")}
                            placeholder={t('settings:labels.form.namePlaceholder')}
                        />
                        {form.formState.errors.name && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.name.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <LabelWidget htmlFor="slug">{t('settings:labels.form.slug')}</LabelWidget>
                        <Input
                            id="slug"
                            {...form.register("slug")}
                            placeholder={t('settings:labels.form.slugPlaceholder')}
                        />
                    </div>

                    <div className="space-y-2">
                        <LabelWidget htmlFor="color">{t('settings:labels.form.color')}</LabelWidget>
                        <div className="flex items-center gap-2">
                            <Input
                                id="color"
                                type="color"
                                value={form.watch("color") || "#3B82F6"}
                                onChange={(e) => form.setValue("color", e.target.value, { shouldValidate: true })}
                                className="w-20 h-10 cursor-pointer"
                            />
                            <Input
                                value={form.watch("color") || "#3B82F6"}
                                onChange={(e) => form.setValue("color", e.target.value, { shouldValidate: true })}
                                placeholder="#3B82F6"
                                className="flex-1"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            {t('common:actions.cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? t('common:messages.loading') : t('common:actions.save')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
